import React, { useEffect, useState } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import { LazyLoadImage } from "react-lazy-load-image-component";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import usePagedShortProducts from "../../../hooks/usePagedShortProducts";

const getItemId = (item) =>
  item?.menuItem_ID ?? item?.MenuItem_ID ?? item?.id;

const normalizeItem = (item) => {
  const id = getItemId(item);
  return {
    ...item,
    menuItem_ID: id,
    MenuItem_ID: id,
    menuItem_Name: item.menuItem_Name || item.MenuItem_Name || "",
    menuItem_Image: item.menuItem_Image || item.MenuItem_Image || "",
    menuItem_SalePrice:
      item.menuItem_SalePrice ?? item.MenuItem_SalePrice ?? 0,
  };
};

export default function ItemAssignmentModal({
  showModal,
  setShowModal,
  modifierGroup,
  modifierGroups,
  compID,
  onSuccess,
  isBulk = false,
}) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const {
    products,
    loading,
    loadingMore,
    hasMore,
    handleScroll,
  } = usePagedShortProducts({
    enabled: !!showModal,
    searchTerm,
    compID,
  });

  const items = products.map(normalizeItem);

  useEffect(() => {
    if (showModal) {
      setSelectedItems([]);
      setSearchTerm("");
    }
  }, [showModal]);

  const handleItemSelection = (item) => {
    const normalized = normalizeItem(item);
    setSelectedItems((prev) => {
      const isSelected = prev.some(
        (selectedItem) => getItemId(selectedItem) === getItemId(normalized)
      );
      if (isSelected) {
        return prev.filter(
          (selectedItem) => getItemId(selectedItem) !== getItemId(normalized)
        );
      }
      return [...prev, normalized];
    });
  };

  const handleSubmit = async () => {
    if (
      (!modifierGroup && !isBulk) ||
      (!modifierGroups && isBulk) ||
      selectedItems.length === 0
    ) {
      showErrorToast("Please select at least one item");
      return;
    }

    try {
      setSubmitting(true);

      if (isBulk) {
        const modifierGroupIDs = modifierGroups.map(
          (group) => group.modifierGroupID
        );
        const itemIDs = selectedItems.map((item) => getItemId(item));

        const payload = {
          modifierGroupIDs: modifierGroupIDs,
          compID: compID,
          itemIDs: itemIDs,
        };

        const response = await axiosInstance.post(
          APIs.POST_BULK_ASSIGN_ITEMS_TO_MODIFIER_GROUPS,
          payload
        );

        if (response.data.status) {
          showSuccessToast(
            `Successfully assigned ${selectedItems.length} item(s) to ${modifierGroups.length} modifier group(s)`
          );
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        } else {
          showErrorToast(
            response.data.message ||
              "Failed to assign items to modifier group "
          );
        }
      } else {
        const payload = {
          modifierGroupID: modifierGroup.modifierGroupID,
          compID: compID,
          itemIDs: selectedItems.map((item) => getItemId(item)),
        };

        const response = await axiosInstance.post(
          APIs.POST_ASSIGN_ITEMS_TO_MODIFIER_GROUP,
          payload
        );

        if (response.data.status) {
          showSuccessToast(
            `Successfully assigned "${modifierGroup.name}   to ${selectedItems.length} item(s) "`
          );
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        } else {
          showErrorToast(
            response.data.message ||
              "Failed to assign modifier group to items"
          );
        }
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          "Failed to assignmodifier group(s) to items"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const RenderItemCard = ({ item }) => {
    const isSelected = selectedItems.some(
      (selectedItem) => getItemId(selectedItem) === getItemId(item)
    );

    return (
      <div
        className="item-card"
        style={{
          background: isSelected ? "#e3f2fd" : "#ffffff",
          borderRadius: "12px",
          padding: "16px 20px",
          border: isSelected ? "2px solid #007bff" : "2px solid #e9ecef",
          transition: "all 0.2s ease",
          cursor: "pointer",
          position: "relative",
          boxShadow: isSelected
            ? "0 4px 12px rgba(0, 123, 255, 0.15)"
            : "0 2px 8px rgba(0, 0, 0, 0.05)",
          marginBottom: "12px",
        }}
        onClick={() => handleItemSelection(item)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div
            style={{
              width: "40px",
              height: "40px",
              background: isSelected ? "#007bff" : "#f8f9fa",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
              border: "1px solid #e9ecef",
              overflow: "hidden",
            }}
          >
            <LazyLoadImage
              src={item.menuItem_Image || "/assets/img/default-product.png"}
              alt={item.menuItem_Name}
              style={{ height: "32px", width: "32px", objectFit: "contain" }}
            />
          </div>
          <div className="flex-grow-1">
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: isSelected ? "#007bff" : "#495057",
                display: "block",
                marginBottom: "2px",
              }}
            >
              {item.menuItem_Name}
            </span>
            {item.menuItem_SalePrice ? (
              <small
                style={{
                  color: isSelected ? "#0056b3" : "#6c757d",
                  fontSize: "11px",
                  lineHeight: "1.3",
                }}
              >
                ${item.menuItem_SalePrice}
              </small>
            ) : null}
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "20px",
            height: "20px",
            borderRadius: "4px",
            border: isSelected ? "2px solid #007bff" : "2px solid #e9ecef",
            background: isSelected ? "#007bff" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <i className="fa fa-check" style={{ fontSize: "10px", color: "white" }} />
          )}
        </div>
      </div>
    );
  };

  if (!modifierGroup && !isBulk) return null;

  const getModalTitle = () => {
    if (isBulk && modifierGroups) {
      return `Assign Items to ${modifierGroups.length} Modifier Groups`;
    }
    return `Assign Items to "${modifierGroup?.name}"`;
  };

  const getModalDescription = () => {
    if (isBulk && modifierGroups) {
      return `Select the items to assign to the following modifier groups: ${modifierGroups
        .map((g) => g.name)
        .join(", ")}`;
    }
    return `Select the items to assign to this modifier group`;
  };

  return (
    <div>
      <CustomModal
        isOpen={showModal}
        title={getModalTitle()}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        isCentered={true}
        submitButtonText={submitting ? "Submitting..." : "Submit"}
        submitButtonDisabled={selectedItems.length === 0 || submitting}
      >
        <p style={{ color: "#6c757d", marginBottom: "20px" }}>
          {getModalDescription()}
        </p>

        <div className="mb-4">
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "12px 16px",
                border: "2px solid #e9ecef",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
            <i
              className="fa fa-search"
              style={{
                position: "absolute",
                right: "16px",
                color: "#6c757d",
                fontSize: "14px",
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                style={{
                  position: "absolute",
                  right: "40px",
                  background: "transparent",
                  border: "none",
                  color: "#6c757d",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                <i className="fa fa-times" style={{ fontSize: "12px" }} />
              </button>
            )}
          </div>
        </div>

        {selectedItems.length > 0 && (
          <div
            className="mb-3"
            style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              padding: "12px 16px",
              border: "1px solid #e9ecef",
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <span style={{ fontSize: "14px", color: "#495057", fontWeight: "500" }}>
                {selectedItems.length} item(s) selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedItems([])}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#dc3545",
                  cursor: "pointer",
                  fontSize: "12px",
                  padding: "4px 8px",
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        <div
          style={{ height: "400px", overflowY: "auto", paddingRight: "8px" }}
          onScroll={handleScroll}
        >
          {loading && items.length === 0 ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2" style={{ color: "#6c757d" }}>
                Loading items...
              </p>
            </div>
          ) : items.length > 0 ? (
            <div>
              {items.map((item) => (
                <RenderItemCard key={getItemId(item)} item={item} />
              ))}
              {loadingMore && (
                <div className="text-center py-3" style={{ color: "#6c757d", fontSize: "13px" }}>
                  Loading more...
                </div>
              )}
              {!hasMore && (
                <div className="text-center py-2" style={{ color: "#adb5bd", fontSize: "12px" }}>
                  End of list
                </div>
              )}
            </div>
          ) : (
            <div
              className="text-center py-5"
              style={{
                background: "#f8f9fa",
                borderRadius: "12px",
                border: "2px dashed #e9ecef",
              }}
            >
              <p style={{ color: "#6c757d", fontSize: "13px", marginBottom: "6px" }}>
                No items found
                {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
              </p>
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
}
